import { useEffect, useState } from "react";
import { Users, ArrowDownRight, ArrowUpRight, Calendar, FileText, Building2, Briefcase } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const CURRENCY_FMT = {
  GBP: { symbol: "£", locale: "en-GB" },
  EUR: { symbol: "€", locale: "de-DE" },
  USD: { symbol: "$", locale: "en-US" },
};

function fmt(amount, currency = "GBP") {
  const cfg = CURRENCY_FMT[currency] || CURRENCY_FMT.GBP;
  const v = Math.abs(Number(amount) || 0);
  return `${cfg.symbol}${v.toLocaleString(cfg.locale, { maximumFractionDigits: 0 })}`;
}

function CounterpartyCard({ c }) {
  const isCustomer = c.type === "customer";
  const total = isCustomer ? c.total_received : c.total_paid;
  const tone = isCustomer
    ? "border-l-[#10B981] bg-emerald-500/[0.04]"
    : "border-l-[#F59E0B] bg-amber-500/[0.04]";
  const accent = isCustomer ? "text-emerald-400" : "text-amber-400";
  const Icon = isCustomer ? Building2 : Briefcase;
  return (
    <div
      className={`rounded-lg border border-slate-800/40 ${tone} border-l-[4px] p-4 hover:bg-slate-900/60 transition-colors`}
      data-testid={`counterparty-${(c.name || "").replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 ${accent} shrink-0`} />
          <h3 className="text-sm font-semibold text-slate-100 truncate" data-testid="counterparty-name">
            {c.name}
          </h3>
        </div>
        <Badge className={`text-[9px] border-none ${isCustomer ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
          {isCustomer ? "Customer" : "Supplier"}
        </Badge>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[9px] text-slate-500 uppercase tracking-[0.12em] mb-0.5">
            Total {isCustomer ? "Received" : "Paid"}
          </div>
          <div className={`text-base font-mono font-bold ${accent}`} data-testid="counterparty-total">
            {fmt(total, c.currency)}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-slate-500 uppercase tracking-[0.12em] mb-0.5">Transactions</div>
          <div className="text-base font-mono text-slate-200">
            {c.transaction_count}
          </div>
        </div>
      </div>
      {c.last_transaction && (
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500 mb-2">
          <Calendar className="w-3 h-3" />
          Last: {new Date(c.last_transaction).toLocaleDateString("en-GB",
            { day: "numeric", month: "short", year: "numeric" })}
          {c.first_transaction && c.first_transaction !== c.last_transaction && (
            <span className="ml-1 text-slate-600">
              · since {new Date(c.first_transaction).toLocaleDateString("en-GB",
                { month: "short", year: "numeric" })}
            </span>
          )}
        </div>
      )}
      {c.references && c.references.length > 0 && (
        <div className="flex items-start gap-1.5 text-[10px] text-slate-500 mb-1">
          <FileText className="w-3 h-3 mt-0.5 shrink-0" />
          <div className="flex flex-wrap gap-1">
            {c.references.slice(0, 6).map((ref) => (
              <span key={ref} className="font-mono text-slate-400 bg-slate-800/50 rounded px-1.5 py-0.5">
                {ref}
              </span>
            ))}
            {c.references.length > 6 && (
              <span className="text-slate-600">+{c.references.length - 6} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CounterpartiesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await api.get("/counterparties");
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancel = true; };
  }, []);

  if (error) {
    return (
      <div className="space-y-4" data-testid="counterparties-page">
        <Header counts={{ customers: 0, suppliers: 0 }} />
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-6 text-sm text-red-400">{error}</CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4" data-testid="counterparties-page">
        <Header counts={{ customers: 0, suppliers: 0 }} loading />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="rounded-lg border border-slate-800/40 bg-slate-900/40 p-4 h-32 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="counterparties-page">
      <Header
        counts={{ customers: data.total_customers, suppliers: data.total_suppliers }}
      />

      {data.customers.length === 0 && data.suppliers.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-8 text-center text-sm text-slate-400" data-testid="counterparties-empty">
            <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="mb-1">No counterparties yet.</p>
            <p className="text-xs text-slate-500">
              Upload bank statements with explicit "Sent money to ..." / "Received money from ..." descriptions and the rolling balances will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {data.customers.length > 0 && (
            <Section
              title="Customers"
              subtitle={`${data.total_customers} active`}
              icon={ArrowDownRight}
              accent="text-emerald-400"
              testid="counterparties-customers"
            >
              {data.customers.map((c) => (
                <CounterpartyCard key={`cust-${c.name}-${c.currency}`} c={c} />
              ))}
            </Section>
          )}

          {data.suppliers.length > 0 && (
            <Section
              title="Suppliers"
              subtitle={`${data.total_suppliers} active`}
              icon={ArrowUpRight}
              accent="text-amber-400"
              testid="counterparties-suppliers"
            >
              {data.suppliers.map((c) => (
                <CounterpartyCard key={`sup-${c.name}-${c.currency}`} c={c} />
              ))}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Header({ counts, loading }) {
  return (
    <div className="flex items-end justify-between gap-4 pb-2 border-b border-slate-800/30">
      <div>
        <h1 className="font-barlow font-bold text-2xl text-slate-100 tracking-tight" data-testid="counterparties-title">
          Counterparties
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Per-counterparty rolling balances. Currencies are kept distinct so EUR / USD / GBP
          inflows from the same customer never get illegally summed.
        </p>
      </div>
      {!loading && (
        <div className="flex items-center gap-3 text-xs text-slate-400 shrink-0">
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            {counts.customers} customers
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            {counts.suppliers} suppliers
          </span>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, accent, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] ${accent}`}>
          <Icon className="w-3.5 h-3.5" /> {title}
        </h2>
        <span className="text-[10px] text-slate-500 font-mono">{subtitle}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {children}
      </div>
    </section>
  );
}
